import type { Feature, FeatureCollection, Geometry } from "geojson";
import { PullInZone } from "./PullInZone";

/**
 * The parsed `Jobs."Map Features GeoJSON (code)"` field: every feature the portal attached to a job
 * outside mapmaking, tagged by `properties.name` (see `MapFeatureName`). Unknown names are kept
 * and ignored, so a future kind written by a newer build never makes this build treat the job as
 * broken. Read-only here — the field is written through the scheduling save's DELTAS
 * (`MapFeatureEdit`), merged server-side.
 */
export class MapFeatures {
	private constructor(readonly features: readonly Feature<Geometry>[]) {}

	static empty(): MapFeatures {
		return new MapFeatures([]);
	}

	/** Lenient: junk in a free-text field is data, not a programming error — null, never a throw. */
	static parse(text: string | null | undefined): MapFeatures | null {
		if (!text || !text.trim()) return null;
		let parsed: unknown;
		try {
			parsed = JSON.parse(text);
		} catch {
			return null;
		}
		if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;
		const fc = parsed as Partial<FeatureCollection>;
		if (fc.type !== "FeatureCollection" || !Array.isArray(fc.features)) return null;
		const features = fc.features.filter(
			(f): f is Feature<Geometry> => !!f && typeof f === "object" && (f as Feature).type === "Feature",
		);
		return new MapFeatures(features);
	}

	get isEmpty(): boolean {
		return this.features.length === 0;
	}

	/** The `pullin` polygons a person would recognise as zones; a `pullin` feature that fails
	 *  `PullInZone.fromFeature` (hole, open ring, junk coordinates) is skipped. */
	pullInZones(): PullInZone[] {
		const zones: PullInZone[] = [];
		for (const f of this.features) {
			if (f.properties?.name !== "pullin") continue;
			const zone = PullInZone.fromFeature(f);
			if (zone) zones.push(zone);
		}
		return zones;
	}
}
